import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { withLogger } from '@/new-transport/_store/logger';
import { withBasicTransport } from '@/new-transport/_store/basic-transport.feature';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { combineLatest, filter, firstValueFrom, forkJoin, map, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { computed, inject } from '@angular/core';
import { TransportTimetableApiService } from '@/new-transport/transport-timetable/_api/transport-timetable.api.service';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
	GroupedTimetableByVehicle,
	GroupedTimetableByVehicleWithStopConnection,
	StopConnection,
	TimetableWithNestedAntennas,
	TransportLogic,
	TransportTimetable,
	TransportVehicleKind,
	VehicleInGroupedTimetable,
} from '@/new-transport/_models/transport-timetable.model';
import { TransportTimetableProcessor } from '@/new-transport/transport-timetable/transport-timetable-processor/transport-timetable-processor';
import { StopKind, Transport, TransportKind, TransportSide } from '@/new-transport/_models/transport.model';
import { withLoadingFeature } from '@/_signal-store-features/loading.feature.store';
import { addStopConnectionsToTimetable } from '@/new-transport/_utils/add-stop-connections-to-timetable/add-stop-connections-to-timetable';
import { getTimetablesWithNestedAntennas } from '@/new-transport/_utils/get-timetables-with-nested-antennas/get-timetables-with-nested-antennas';
import { TimetableCalculator } from '@/new-transport/transport-timetable/timetable-calculator/timetable-calculator';
import { updateTimetableByProperty } from '@/new-transport/_utils/update-timetable-by-property/update-timetable-by-property';
import { MatSnackBar } from '@angular/material/snack-bar';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { getTransportTimingBySide } from '@/new-transport/_utils/get-transport-timing-by-side/get-transport-timing-by-side';

export const MESSAGE_CHECK_IN_ERROR = 'Transport has to arrive on the same day as the check-in date';

export const MESSAGE_CHECK_OUT_ERROR = 'Return transport has to start on the same day as the check-out date';

export type TransportTimetableState = {
	groupedVehicles: GroupedTimetableByVehicle[];
	stopsConnections: StopConnection[];
	calculateTimeDepartures: boolean;
	transportVehicles: TransportLogic['vehicle'][];
};

const transportTimetableInitialState: TransportTimetableState = {
	groupedVehicles: [],
	stopsConnections: [],
	calculateTimeDepartures: false,
	transportVehicles: [],
};

export const TransportTimetableStore = signalStore(
	withState(transportTimetableInitialState),
	withLogger('timetable'),
	withLoadingFeature({ timetableIsLoading: false, stopsConnectionsIsLoading: false, vehicleIsLoading: false }),
	withBasicTransport(),
	withComputed(({ groupedVehicles, stopsConnections }) => ({
		timeDepartureMap: computed(
			() =>
				new Map(groupedVehicles().map((v) => [v.vehicle.id, v.timetable.map((entry) => entry.timeDeparture)])),
		),
		timetablesWithNestedAntennas: computed(() => {
			const filterBusTimetables = (groupedVehicles: GroupedTimetableByVehicleWithStopConnection[]) =>
				groupedVehicles.filter(
					(timetable) => timetable.vehicle.transportVehicleKind === TransportVehicleKind.BUS,
				);

			const processTimetables = (): TimetableWithNestedAntennas[] => {
				const groupedVehiclesWithStopConnection = groupedVehicles().map((v) =>
					addStopConnectionsToTimetable(v, stopsConnections()),
				);

				const busTimetables = filterBusTimetables(groupedVehiclesWithStopConnection);

				return getTimetablesWithNestedAntennas(busTimetables, groupedVehiclesWithStopConnection);
			};

			return processTimetables();
		}),
		boardingTimetables: computed(() =>
			groupedVehicles()
				.flatMap((g) => g.timetable ?? [])
				.filter((t) => t.stop.stopType === StopKind.BOARDING),
		),
	})),
	withMethods(
		(
			store,
			apiService = inject(TransportTimetableApiService),
			_snackBar = inject(MatSnackBar),
			infoboxService = inject(InfoboxService),
		) => ({
			updateStopSale(stopSale: boolean, timetableId: number, vehicleId: number) {
				const timetableCalculator = new TimetableCalculator(
					store.groupedVehicles(),
					store.stopsConnections(),
					store.transport(),
					_snackBar,
					infoboxService,
				);

				const group = store.groupedVehicles().find((g) => g.vehicle.id === vehicleId);
				if (!group) {
					return;
				}

				const entry = group.timetable.find((e) => e.id === timetableId);
				if (!entry) {
					return;
				}

				entry.stopSale = stopSale;

				const timetableEntry = timetableCalculator.processTimetablesToCalculateWithAntennas(
					timetableId,
					vehicleId,
				);

				patchState(store, (state) => ({
					groupedVehicles: state.groupedVehicles.map((grouped) => {
						if (!timetableEntry.some((v) => v.vehicleId === grouped.vehicle.id)) {
							return grouped;
						}

						grouped.timetable.forEach((t) => {
							if (t.stop.id === entry.stop.id) {
								t.stopSale = stopSale;
							}
						});

						return grouped;
					}),
				}));
			},
			updateFee(fee: number, timetableId: number, vehicleId: number) {
				patchState(store, (state) => ({
					groupedVehicles: updateTimetableByProperty<number>(
						state.groupedVehicles,
						'fee',
						vehicleId,
						timetableId,
						fee,
					),
				}));
			},
			updateFlightNumber(flightNumber: string, timetableId: number, vehicleId: number) {
				patchState(store, (state) => ({
					groupedVehicles: updateTimetableByProperty<string>(
						state.groupedVehicles,
						'flightNumber',
						vehicleId,
						timetableId,
						flightNumber,
					),
				}));
			},

			updatePauseDuration(pauseDuration: number, timetableId: number, vehicleId: number) {
				patchState(store, (state) => ({
					groupedVehicles: updateTimetableByProperty<number>(
						state.groupedVehicles,
						'pauseDuration',
						vehicleId,
						timetableId,
						pauseDuration,
					),
				}));
			},
			updateTimeDeparture(
				timeDeparture: string,
				timetableId: number,
				vehicleId: number,
				timetableCalculator = new TimetableCalculator(
					store.groupedVehicles(),
					store.stopsConnections(),
					store.transport(),
					_snackBar,
					infoboxService,
				),
			) {
				if (!store.calculateTimeDepartures()) {
					return {
						groupedVehicles: store.groupedVehicles().map((group) => {
							if (group.vehicle.id === vehicleId) {
								group.timetable = group.timetable.map((v) =>
									v.id === timetableId ? { ...v, timeDeparture: timeDeparture } : v,
								);
							}
							return group;
						}),
					};
				}

				const groupedVehicles = timetableCalculator.calculateRelatedTimeDepartures(
					vehicleId,
					timetableId,
					timeDeparture,
				);

				patchState(store, () => ({
					groupedVehicles,
				}));
			},
			updateCalculateTimeDepartures(calculateTimeDepartures: boolean) {
				patchState(store, (state) => ({
					...state,
					calculateTimeDepartures,
				}));
			},
			updateSelectedVehicle(currentVehicle: VehicleInGroupedTimetable, newVehicleId: number): void {
				const { groupedVehicles, transportVehicles } = store;

				const updatedGroupedVehicles = groupedVehicles().map((groupedVehicle) => {
					const isTargetVehicle = groupedVehicle.vehicle.id === currentVehicle.id;

					const updatedAntennas = groupedVehicle.antennasForVehicle.map((antenna) => ({
						...antenna,
						vehicle: antenna.vehicle === currentVehicle.id ? newVehicleId : antenna.vehicle,
					}));

					if (!isTargetVehicle) {
						return { ...groupedVehicle, antennasForVehicle: updatedAntennas };
					}

					const newVehicle = transportVehicles().find((v) => v.id === newVehicleId);

					if (!newVehicle) {
						return { ...groupedVehicle, antennasForVehicle: updatedAntennas };
					}

					return {
						...groupedVehicle,
						vehicle: {
							...newVehicle,
							transportVehicleKind: groupedVehicle.vehicle.transportVehicleKind,
						},
						transportLogic: groupedVehicle.transportLogic.map((logic) => ({
							...logic,
							vehicle: newVehicle,
						})),
						antennasForVehicle: updatedAntennas,
					};
				});

				patchState(store, () => ({
					groupedVehicles: updatedGroupedVehicles,
				}));
			},
			initializeTimetableData: rxMethod<{ id: number; transport: Transport }>(
				pipe(
					switchMap(({ id, transport }) =>
						forkJoin({
							timetable: apiService.getTransportTimetable(id),
							transportLogic: apiService.getTransportLogic(id),
						}).pipe(
							tapResponse({
								next: (
									{ timetable, transportLogic },
									timetableProcessor = new TransportTimetableProcessor(),
								) => {
									patchState(store, () => ({
										groupedVehicles: timetableProcessor.groupAndSortTimetableData(
											timetable,
											transport?.side === TransportSide.DEPARTURE
												? transportLogic
												: transportLogic.reverse(),
											transport,
										),
									}));
								},
								error: () => {
									throw new Error('Wystąpił problem przy pobieraniu trasy');
								},
								finalize: () => {
									patchState(store, (state) => ({
										...state,
										timetableIsLoading: false,
									}));
								},
							}),
						),
					),
				),
			),
			fetchStopsConnections: rxMethod<{ stopFrom: number; stopTo: number }[]>(
				pipe(
					switchMap((ids) => {
						const requests = ids.map((id) => apiService.getStopConnection(id.stopFrom, id.stopTo));
						return forkJoin(requests).pipe(
							tapResponse({
								next: (results) => {
									patchState(store, () => ({
										stopsConnections: results.flatMap((v) => v),
									}));
								},
								error: () => {
									throw new Error('Wystąpił problem przy pobieraniu połączeń między przystankami');
								},
								finalize: () => {
									store.changeLoadingState('stopsConnections', false);
								},
							}),
						);
					}),
				),
			),
			fetchTransportVehicles: rxMethod<void>(
				pipe(
					switchMap(() => {
						return apiService.getTransportVehicles().pipe(
							tapResponse({
								next: (results) => {
									patchState(store, () => ({
										transportVehicles: results.results.map((vehicle) => ({
											id: vehicle.id,
											erpId: vehicle.erpId,
											name: vehicle.name,
											vehicleKind: vehicle.vehicleKind,
										})),
									}));
								},
								error: () => {
									throw new Error('Wystąpił problem przy pobieraniu pojazdów');
								},
								finalize: () => {
									store.changeLoadingState('vehicle', false);
								},
							}),
						);
					}),
				),
			),
			updateStopConnection: rxMethod<StopConnection>(
				pipe(
					tap(() => store.changeLoadingState('stopsConnections', true)),
					switchMap((stopConnection) =>
						apiService[stopConnection.id ? 'updateStopConnection' : 'createStopConnection'](
							stopConnection,
						).pipe(
							tapResponse({
								next: (result) => {
									patchState(store, (state) => {
										const connectionExists = state.stopsConnections.some(
											(connection) => connection.id === result.id,
										);

										return {
											stopsConnections: connectionExists
												? state.stopsConnections.map((connection) =>
														connection.id === result.id ? result : connection,
													)
												: [...state.stopsConnections, result],
										};
									});
								},
								error: (error) => {
									throw new Error(
										`Nie udało się zapisać czasu między przystankami: ${JSON.stringify(error)}`,
									);
								},
								finalize: () => {
									store.changeLoadingState('stopsConnections', false);
								},
							}),
						),
					),
				),
			),
			updateTimetables: rxMethod<TransportTimetable[]>(
				pipe(
					tap(() => store.changeLoadingState('timetable', true)),
					switchMap((timetables) => {
						const transportLogicPayload = store.groupedVehicles().flatMap((v) => {
							return v.transportLogic.map((logic) => ({
								...logic,
								vehicle: logic.vehicle.id,
							}));
						});
						return combineLatest(
							store.transport().kind === TransportKind.TEMPLATE
								? [
										apiService.updateTransportLogic(transportLogicPayload),
										apiService.updateTransportTimetable(timetables),
									]
								: [
										apiService.updateTransportLogic(transportLogicPayload),
										apiService.updateTransportTimetable(timetables),
									],
						).pipe(
							switchMap(([logicUpdateResult, timetableUpdateResult]) =>
								apiService.getTransportTimetable(store.transport().id).pipe(
									map((updatedTimetable) => ({
										logicUpdateResult,
										timetableUpdateResult,
										updatedTimetable,
									})),
								),
							),

							tapResponse({
								next: (
									{ updatedTimetable },
									timetableProcessor = new TransportTimetableProcessor(),
								) => {
									infoboxService.success('Zapisano');

									patchState(store, () => {
										const transportLogics = store
											.groupedVehicles()
											.flatMap((v) => v.transportLogic);
										return {
											groupedVehicles: timetableProcessor.groupAndSortTimetableData(
												updatedTimetable,
												store.transport()?.side === TransportSide.DEPARTURE
													? transportLogics
													: transportLogics.reverse(),
												store.transport(),
											),
										};
									});
								},
								error: (error: { error: unknown[] }) => {
									console.error(JSON.stringify(error));

									const hasMessage = error?.error.some((obj) =>
										Object.values(obj)
											.flat()
											.includes(MESSAGE_CHECK_IN_ERROR || MESSAGE_CHECK_OUT_ERROR),
									);

									const errorMessage = hasMessage
										? 'Data transportu musi odpowiadać dacie przyjazdu lub wyjazdu z obiektu'
										: `Błąd przy zapisie rozkładu: <br>${JSON.stringify(error)}`;

									throw new Error(errorMessage);
								},
								finalize: () => {
									store.changeLoadingState('timetable', false);
								},
							}),
						);
					}),
				),
			),
		}),
	),
	withHooks({
		onInit(
			{ initializeTimetableData, fetchTransportVehicles, changeLoadingState, transport },
			activatedRoute = inject(ActivatedRoute),
		) {
			const transport$ = toObservable(transport);
			changeLoadingState('timetable', true);
			changeLoadingState('stopsConnections', true);
			changeLoadingState('vehicle', true);
			combineLatest([activatedRoute.params, transport$])
				.pipe(
					map(([params, transport]) => ({
						id: Number.isNaN(+params['id']) ? null : +params['id'],
						transport: transport,
					})),
					filter(({ id, transport }) => !!id && !!transport),
					takeUntilDestroyed(),
				)
				.subscribe(({ id, transport }) => {
					initializeTimetableData({ id, transport });
					fetchTransportVehicles();
				});
		},
	}),
);
